import { format } from "date-fns";
import { CalendarDays, Truck } from "lucide-react";
import {
  Card,
  CardContent,
  Skeleton,
  Text,
} from "@musa.tech/design-system";
import { useDemoMode } from "@/contexts/DemoModeContext";
import { maskEstablishmentName } from "@/utils/demoMode";
import { removeGeneratorPrefix } from "@/utils/establishmentUtils";
import { parseCollectDate } from "@/utils/coletasCalendar";
import type { CollectSummaryItem } from "@/integrations/agreements/client";

interface ProximasSummaryCardsProps {
  proximaColeta: CollectSummaryItem | null;
  totalPeriodo: number;
  labelPeriodo: string;
  isResumoLoading: boolean;
  isTotalLoading: boolean;
}

export const ProximasSummaryCards = ({
  proximaColeta,
  totalPeriodo,
  labelPeriodo,
  isResumoLoading,
  isTotalLoading,
}: ProximasSummaryCardsProps) => {
  const { isDemoMode } = useDemoMode();

  const proximaDate = parseCollectDate(proximaColeta?.planned_date);
  const rawName = proximaColeta?.establishment_name || "";
  const proximaName = maskEstablishmentName(
    removeGeneratorPrefix(rawName, isDemoMode),
    isDemoMode,
  );

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Card>
        <CardContent className="flex items-start gap-3 p-4">
          <div className="rounded-md bg-primary-50 p-2 text-primary-600">
            <CalendarDays className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <Text variant="label" size="small" className="text-neutral-500">
              Próxima Coleta
            </Text>
            {isResumoLoading ? (
              <Skeleton className="mt-1 h-7 w-40" />
            ) : proximaColeta && proximaDate ? (
              <>
                <Text
                  variant="heading"
                  size="large"
                  className="block text-neutral-900"
                >
                  {format(proximaDate, "dd/MM/yyyy")}
                </Text>
                <Text
                  variant="body"
                  size="small"
                  className="block truncate text-neutral-600"
                >
                  {proximaName || "—"}
                </Text>
              </>
            ) : (
              <Text variant="body" className="mt-1 block text-neutral-500">
                Nenhuma coleta agendada
              </Text>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex items-start gap-3 p-4">
          <div className="rounded-md bg-primary-50 p-2 text-primary-600">
            <Truck className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <Text variant="label" size="small" className="text-neutral-500">
              Total de Coletas
            </Text>
            {isTotalLoading ? (
              <Skeleton className="mt-1 h-7 w-16" />
            ) : (
              <Text
                variant="heading"
                size="large"
                className="block text-neutral-900"
              >
                {totalPeriodo}
              </Text>
            )}
            <Text variant="body" size="small" className="block text-neutral-500">
              {labelPeriodo}
            </Text>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
